import { useState, useEffect, useCallback } from "react";
import { useBusinessUnitApi } from "../../../hooks/useBusinessUnitApi";

export const useBusinessUnitsViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const {
    getBusinessUnits,
    createBusinessUnit: apiCreateBusinessUnit,
    updateBusinessUnit: apiUpdateBusinessUnit,
    deleteBusinessUnit,
    cacheOperations: { readBusinessUnits, writeBusinessUnits },
  } = useBusinessUnitApi();

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const [businessUnitsResult] = await Promise.all([getBusinessUnits()]);

      if (!businessUnitsResult.success) {
        throw new Error("Failed to fetch required data");
      }

      writeBusinessUnits(businessUnitsResult.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [getBusinessUnits, writeBusinessUnits, loading]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateBusinessUnit = useCallback(
    async (formData) => {
      try {
        const result = await apiCreateBusinessUnit(formData);
        if (result.success) {
          setSuccessModal("Business Unit created successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error creating business unit:", error);
        throw new Error(error.message);
      }
    },
    [apiCreateBusinessUnit, readBusinessUnits, writeBusinessUnits]
  );

  const handleUpdateBusinessUnit = useCallback(
    async (businessUnitId, formData) => {
      try {
        const result = await apiUpdateBusinessUnit(businessUnitId, formData);
        if (result.success) {
          setSuccessModal("Business unit updated successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error updating business unit:", error);
        throw new Error(error.message);
      }
    },
    [apiUpdateBusinessUnit, readBusinessUnits, writeBusinessUnits]
  );

  const handleDelete = useCallback(
    async (businessUnit) => {
      try {
        const result = await deleteBusinessUnit(businessUnit.business_unit_id);
        if (result.success) {         
          setSuccessModal("Business unit deleted successfully");
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error deleting business unit:", err);
      }
    },
    [deleteBusinessUnit, readBusinessUnits, writeBusinessUnits]
  );

  return {
    businessUnits: readBusinessUnits(),
    loading,
    error,
    createBusinessUnit: handleCreateBusinessUnit,
    updateBusinessUnit: handleUpdateBusinessUnit,
    handleDelete,
    successModal,
    setSuccessModal,
    refetch: fetchData,
  };
};
